import { Box, Grid } from '@mui/material';
import type { Filters, PageConfig, Section } from '../types';
import { useSectionAction } from '../hooks/useSectionAction';
import { getSectionActions } from '../utils/getSectionActions';
import Metric from './Metric';
import Insight from './Insight';
import SectionHeaderWithTitle from './SectionHeaderWithTitle';

type Props = {
  page: PageConfig;
  filters?: Filters;
};

const gridColumns = (layout: Section['layout']): number => {
  switch (layout) {
    case 'grid-1':
      return 12;
    case 'grid-2':
      return 6;
    case 'grid-3':
      return 4;
    case 'grid-4':
      return 3;
    default:
      return 12;
  }
};

const spanToMd = (span: Section['items'][number]['span'], fallback: number): number => {
  if (span === 'full') return 12;
  if (typeof span === 'number') return Math.min(12, span * 3);
  return fallback;
};

const isTitleFromSoleItem = (section: Section): boolean =>
  Boolean(section.titleFromMetric && section.items.length === 1);

export default function PageRenderer({ page, filters }: Props) {
  const { handleSectionAction } = useSectionAction();

  return (
    <Box>
      {page.sections.map(section => {
        const col = gridColumns(section.layout);
        const hideItemTitle = isTitleFromSoleItem(section);
        const actions = getSectionActions(section);
        const hasNavigableAction = actions.some(action => Boolean(action.navigation));
        return (
          <Box key={section.id} sx={{ mb: 4 }}>
            <SectionHeaderWithTitle
              section={section}
              onActionClick={
                hasNavigableAction
                  ? action => {
                      void handleSectionAction(section, action);
                    }
                  : undefined
              }
            />
            <Grid container spacing={2} alignItems="stretch">
              {section.items.map(item => (
                <Grid
                  item
                  xs={12}
                  sm={section.layout === 'grid-1' ? 12 : 6}
                  md={spanToMd(item.span, col)}
                  key={`${item.kind}-${item.id}`}
                  sx={{ display: 'flex' }}
                >
                  <Box sx={{ width: '100%', display: 'flex' }}>
                    {item.kind === 'metric' ? (
                      <Metric
                        id={item.id}
                        filters={filters}
                        span={item.span}
                        renderer={item.renderer}
                        presentation={item.presentation}
                        hideTitle={hideItemTitle}
                      />
                    ) : (
                      <Insight id={item.id} filters={filters} hideTitle={hideItemTitle} />
                    )}
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        );
      })}
    </Box>
  );
}
